import { STATUS_LABEL, STATUS_ORDER } from "@/projects/types";
import type {
  ScopedTask,
  TaskPriority,
  TaskStatus,
} from "@/projects/types";

/**
 * The Issues list's DISPLAY state — how the rows are laid out, as opposed to
 * which rows survive (that's the filter state in `tasks-state.ts`).
 *
 * It lives here, in one pure module, because two components that never talk to
 * each other have to agree on it: the `<DisplayMenu>` in the title row writes it,
 * and `<UnifiedIssuesList>` in the body reads it. Both go through the URL, so the
 * only way they can't drift is a shared vocabulary — these option lists ARE what
 * the menu renders and what the list switches on.
 *
 * URL keys: `?group=` `?sort=` `?props=` `?empty=`. Every one is omitted at its
 * default, so an untouched Issues page keeps a clean URL.
 */

export type IssueGrouping = "status" | "assignee" | "project" | "none";
export type IssueOrdering = "updated" | "created" | "due" | "priority" | "title";
/** A column of the lifecycle row the user can hide. Mirrors `LifecycleRowModel`'s
 *  optional display fields — hiding one strips that field from the model, so the
 *  row component itself needs no per-column props. */
export type IssueProperty =
  | "priority"
  | "status"
  | "project"
  | "section"
  | "assignee"
  | "date";

export const GROUPING_OPTIONS: { value: IssueGrouping; label: string }[] = [
  { value: "status", label: "Status" },
  { value: "assignee", label: "Assignee" },
  { value: "project", label: "Project" },
  { value: "none", label: "No grouping" },
];

export const ORDERING_OPTIONS: { value: IssueOrdering; label: string }[] = [
  { value: "updated", label: "Last updated" },
  { value: "created", label: "Newest" },
  { value: "due", label: "Due date" },
  { value: "priority", label: "Priority" },
  { value: "title", label: "Title" },
];

export const PROPERTY_OPTIONS: { value: IssueProperty; label: string }[] = [
  { value: "priority", label: "Priority" },
  { value: "status", label: "Status" },
  { value: "project", label: "Project" },
  { value: "section", label: "Section" },
  { value: "assignee", label: "Assignee" },
  { value: "date", label: "Date" },
];

const DEFAULT_GROUPING: IssueGrouping = "status";
const DEFAULT_ORDERING: IssueOrdering = "updated";
/** Everything on — the row as it renders today. */
export const DEFAULT_PROPERTIES: IssueProperty[] = PROPERTY_OPTIONS.map(
  (p) => p.value,
);
/** `?props=` sentinel for "every column hidden" — an empty param would be
 *  indistinguishable from an absent one, which means "all". */
const NO_PROPERTIES = "none";

export interface IssuesDisplayState {
  grouping: IssueGrouping;
  ordering: IssueOrdering;
  properties: IssueProperty[];
  showEmpty: boolean;
}

const GROUPINGS = new Set(GROUPING_OPTIONS.map((o) => o.value));
const ORDERINGS = new Set(ORDERING_OPTIONS.map((o) => o.value));
const PROPERTIES = new Set(PROPERTY_OPTIONS.map((o) => o.value));

/** Read the display state off any param bag (`URLSearchParams`, a plain record). */
export function resolveIssuesDisplay(
  get: (key: string) => string | null | undefined,
): IssuesDisplayState {
  const rawGroup = get("group");
  const rawSort = get("sort");
  const rawProps = get("props");

  let properties = DEFAULT_PROPERTIES;
  if (rawProps === NO_PROPERTIES) properties = [];
  else if (rawProps) {
    properties = rawProps
      .split(",")
      .map((p) => p.trim())
      .filter((p): p is IssueProperty => PROPERTIES.has(p as IssueProperty));
  }

  return {
    grouping: GROUPINGS.has(rawGroup as IssueGrouping)
      ? (rawGroup as IssueGrouping)
      : DEFAULT_GROUPING,
    ordering: ORDERINGS.has(rawSort as IssueOrdering)
      ? (rawSort as IssueOrdering)
      : DEFAULT_ORDERING,
    properties,
    showEmpty: get("empty") === "1",
  };
}

/** Turn a display change into query updates — `null` drops the key, so a value
 *  back at its default leaves no trace in the URL. */
export function issuesDisplayParams(
  next: Partial<IssuesDisplayState>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  if (next.grouping !== undefined)
    out.group = next.grouping === DEFAULT_GROUPING ? null : next.grouping;
  if (next.ordering !== undefined)
    out.sort = next.ordering === DEFAULT_ORDERING ? null : next.ordering;
  if (next.showEmpty !== undefined) out.empty = next.showEmpty ? "1" : null;
  if (next.properties !== undefined) {
    const p = next.properties;
    out.props =
      p.length === 0
        ? NO_PROPERTIES
        : p.length === DEFAULT_PROPERTIES.length &&
            DEFAULT_PROPERTIES.every((d) => p.includes(d))
          ? null
          : p.join(",");
  }
  return out;
}

/* ------------------------------------------------------------------ ordering */

const PRIORITY_RANK: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

/** Newest-first for the timestamps, most-urgent-first for priority, A→Z for
 *  title. A missing due date sorts last rather than first — an undated issue
 *  isn't the most urgent thing on the page. */
export function orderTasks(
  tasks: ScopedTask[],
  ordering: IssueOrdering,
): ScopedTask[] {
  const by: Record<IssueOrdering, (a: ScopedTask, b: ScopedTask) => number> = {
    updated: (a, b) => b.updated_at.localeCompare(a.updated_at),
    created: (a, b) => b.created_at.localeCompare(a.created_at),
    due: (a, b) =>
      (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999") ||
      b.updated_at.localeCompare(a.updated_at),
    priority: (a, b) =>
      PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
      b.updated_at.localeCompare(a.updated_at),
    title: (a, b) => a.title.localeCompare(b.title),
  };
  return [...tasks].sort(by[ordering]);
}

/* ------------------------------------------------------------------ grouping */

export interface IssueGroup {
  key: string;
  label: string;
  tasks: ScopedTask[];
}

const UNASSIGNED = "\x00unassigned";

/**
 * Group the ordered rows. Status uses the canonical `STATUS_ORDER` so the groups
 * read as a pipeline and can include EMPTY buckets (that's what `showEmpty` is
 * for); assignee/project derive their groups from the data, where an empty group
 * cannot exist by construction.
 */
export function groupTasks(
  tasks: ScopedTask[],
  grouping: IssueGrouping,
  showEmpty: boolean,
): IssueGroup[] {
  if (grouping === "none") return [{ key: "all", label: "", tasks }];

  if (grouping === "status") {
    const byStatus = new Map<TaskStatus, ScopedTask[]>();
    for (const t of tasks)
      byStatus.set(t.status, [...(byStatus.get(t.status) ?? []), t]);
    return STATUS_ORDER.filter(
      (s: TaskStatus) => showEmpty || (byStatus.get(s)?.length ?? 0) > 0,
    ).map((s: TaskStatus) => ({
      key: s,
      label: STATUS_LABEL[s],
      tasks: byStatus.get(s) ?? [],
    }));
  }

  const keyOf = (t: ScopedTask) =>
    grouping === "assignee" ? (t.assignee ?? UNASSIGNED) : t.project_slug;
  const labelOf = (t: ScopedTask) =>
    grouping === "assignee"
      ? (t.assignee_label ?? t.assignee ?? "Unassigned")
      : t.project_name;

  const groups = new Map<string, IssueGroup>();
  for (const t of tasks) {
    const key = keyOf(t);
    const g: IssueGroup = groups.get(key) ?? { key, label: labelOf(t), tasks: [] };
    g.tasks.push(t);
    groups.set(key, g);
  }
  // Biggest first, with the unassigned bucket pinned last — it's a residue, not
  // a peer of the named groups.
  return [...groups.values()].sort((a, b) =>
    a.key === UNASSIGNED
      ? 1
      : b.key === UNASSIGNED
        ? -1
        : b.tasks.length - a.tasks.length,
  );
}
